import { useEffect, useRef } from "react";
import * as THREE from "three";
import mapboxgl from "mapbox-gl";
import { useMap } from "../../contexts/MapContext";
import { buildElevGrid, elevAt, type ElevGrid } from "./fuel3d/elevGrid";

/**
 * 3D rendering of building assets for the Assets layer. ONLY assets whose
 * `asset_type` is "Building" are extruded — roads, transmission lines, power
 * stations etc. stay 2D. The asset vector tiles carry polygon footprints (and
 * some point assets) but no height attribute, so each footprint is extruded to
 * a height derived from its plan area with a deterministic per-building jitter
 * — bigger footprints read as taller, and a building keeps its exact
 * height/colour across rebuilds (the jitter is hashed from its rounded
 * centroid, never re-rolled on pan).
 *
 * Rendering mirrors FuelLoad3DTrees: a Mapbox custom layer sharing the map's
 * WebGL2 context via three.js, the same mercator→clip anchor-matrix recipe, and
 * a terrain-following invisible shadow receiver so buildings cast real shadows.
 * Building-type point assets get a small default square footprint so they
 * still render in 3D.
 */

interface Assets3DBuildingsProps {
  /** Source id of the assets vector source (same as the fill layer id). */
  sourceId: string;
  /** Attach the 3D buildings layer — true while the "3D Layers" toggle is on. */
  active: boolean;
  /** Show the extruded buildings — the "Buildings" option within that layer. */
  visible: boolean;
}

const SOURCE_LAYER = "assets";

/** Only this asset type is extruded (matches the backend Asset.AssetType enum). */
function isBuilding(props: GeoJSON.GeoJsonProperties): boolean {
  const t = props?.asset_type ?? props?.assetType;
  return typeof t === "string" && t.trim().toLowerCase() === "building";
}
/** Below this zoom buildings are hidden — too small to read, too many to plant. */
const MIN_ZOOM = 13;
/** Hard cap on extruded buildings per rebuild. */
const MAX_BUILDINGS = 20000;
/** Terrain source id added in Map.tsx — its tile loads gate building elevation. */
const DEM_SOURCE_ID = "mapbox-dem";
/** Default footprint half-size (m) for point assets that carry no polygon. */
const POINT_HALF_M = 4.5;
/**
 * Building bases and the shadow receiver sample terrain from one coarse grid
 * built once per rebuild (see fuel3d/elevGrid) — the same scheme the 3D trees
 * use — instead of a queryTerrainElevation raycast per building plus ~25k
 * per-vertex raycasts for the receiver (the dominant rebuild cost here).
 */
const ELEV_GRID_N = 96;
/** Shadow-receiver tessellation (was 160²; the grid does the heavy lifting). */
const RECEIVER_SEGMENTS = 96;

// Wall / roof palette. Warm rendered walls; roofs split between light grey and
// terracotta (hash-chosen) so they stay bright against the landscape. Each
// building is hue/tone jittered per its hashed centroid so a street doesn't
// read as clones.
const WALL = new THREE.Color("#e3d9c5");
const ROOF_GREY = new THREE.Color("#cfc9c0");
const ROOF_TERRA = new THREE.Color("#c98a6b");
/** Highlight outline — matches the 2D asset layer's #AF0 accent. */
const HIGHLIGHT = new THREE.Color("#aaff00");

function clamp(v: number, a: number, b: number) {
  return Math.min(b, Math.max(a, v));
}

/** Deterministic [0,1) hash of an integer cell — stable across rebuilds. */
function hash2(x: number, z: number): number {
  let h = (Math.imul(x, 374761393) + Math.imul(z, 668265263)) | 0;
  h = (h ^ (h >>> 13)) | 0;
  h = Math.imul(h, 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

type Bbox = [number, number, number, number];

/** One footprint reduced to scene-local metres (x east, z south) + metadata. */
interface Footprint {
  ring: Array<[number, number]>; // scene-local metres, no closing dup
  cx: number; // centroid x (scene metres)
  cz: number; // centroid z (scene metres)
  area: number; // plan area (m²)
  mercCx: number; // centroid mercator x (for terrain lookup)
  mercCy: number; // centroid mercator y
  hkey: number; // stable hash seed from rounded centroid
}

/** Append the outer ring of a polygon as a scene-local footprint. */
function ringToFootprint(
  coords: GeoJSON.Position[],
  refX: number,
  refY: number,
  scale: number
): Footprint | null {
  const n = coords.length;
  if (n < 4) return null; // need at least a triangle + closing vertex
  const ring: Array<[number, number]> = [];
  let cx = 0;
  let cz = 0;
  let mercCx = 0;
  let mercCy = 0;
  // Drop the closing vertex (GeoJSON repeats the first point).
  const last = n - 1;
  for (let i = 0; i < last; i++) {
    const m = mapboxgl.MercatorCoordinate.fromLngLat(
      coords[i] as [number, number]
    );
    const sx = (m.x - refX) / scale;
    const sz = (m.y - refY) / scale;
    ring.push([sx, sz]);
    cx += sx;
    cz += sz;
    mercCx += m.x;
    mercCy += m.y;
  }
  const count = ring.length;
  if (count < 3) return null;
  cx /= count;
  cz /= count;
  mercCx /= count;
  mercCy /= count;
  // Shoelace area in scene metres².
  let s = 0;
  for (let i = 0, j = count - 1; i < count; j = i++) {
    s += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
  }
  const area = Math.abs(s) / 2;
  if (area < 4) return null; // sub-4 m² slivers aren't buildings
  const hkey = Math.round(mercCx * 1e7) * 73856093 + Math.round(mercCy * 1e7);
  return { ring, cx, cz, area, mercCx, mercCy, hkey: hkey | 0 };
}

/** Square footprint for a point asset, centred on the point. */
function pointToFootprint(
  pt: GeoJSON.Position,
  refX: number,
  refY: number,
  scale: number
): Footprint {
  const m = mapboxgl.MercatorCoordinate.fromLngLat(pt as [number, number]);
  const cx = (m.x - refX) / scale;
  const cz = (m.y - refY) / scale;
  const h = POINT_HALF_M;
  const ring: Array<[number, number]> = [
    [cx - h, cz - h],
    [cx + h, cz - h],
    [cx + h, cz + h],
    [cx - h, cz + h],
  ];
  const hkey = Math.round(m.x * 1e7) * 73856093 + Math.round(m.y * 1e7);
  return {
    ring,
    cx,
    cz,
    area: (h * 2) * (h * 2),
    mercCx: m.x,
    mercCy: m.y,
    hkey: hkey | 0,
  };
}

/** Building height (m) from plan area + a stable per-building jitter. */
function buildingHeight(area: number, hkey: number): number {
  const jitter = 0.85 + 0.3 * hash2(hkey, hkey >> 7);
  return clamp(3.5 + Math.sqrt(area) * 0.18, 3.5, 28) * jitter;
}

// ---------------------------------------------------------------------------
// Custom layer
// ---------------------------------------------------------------------------
class AssetBuildingsLayer implements mapboxgl.CustomLayerInterface {
  readonly id: string;
  readonly type = "custom" as const;
  readonly renderingMode = "3d" as const;

  private sourceId: string;
  private map: mapboxgl.Map | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.Camera | null = null;
  private group: THREE.Group | null = null;
  private sun: THREE.DirectionalLight | null = null;
  private shadowGround: THREE.Mesh | null = null;
  private ref: { x: number; y: number; scale: number } | null = null;
  /** Anchor matrix (mercator→scene metres); depends only on `ref`, so it is
   *  rebuilt on rebuild() rather than every frame in render(). */
  private anchor = new THREE.Matrix4();
  private scratch = new THREE.Matrix4();
  private capWarned = false;

  constructor(id: string, sourceId: string) {
    this.id = id;
    this.sourceId = sourceId;
  }

  onAdd(map: mapboxgl.Map, gl: WebGL2RenderingContext) {
    this.map = map;
    this.camera = new THREE.Camera();
    this.scene = new THREE.Scene();
    this.group = new THREE.Group();
    this.scene.add(this.group);
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x3a3024, 0.9));
    const sun = new THREE.DirectionalLight(0xfff4e0, 1.45);
    sun.position.set(-400, 700, -300);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.bias = -0.0008;
    sun.shadow.normalBias = 0.6;
    this.scene.add(sun);
    this.scene.add(sun.target);
    this.sun = sun;
    this.renderer = new THREE.WebGLRenderer({
      canvas: map.getCanvas(),
      context: gl,
    });
    this.renderer.autoClear = false;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // Buildings are static between rebuilds — render the shadow map once per
    // rebuild (needsUpdate in rebuild()) rather than every frame.
    this.renderer.shadowMap.autoUpdate = false;
  }

  onRemove() {
    this.clearMeshes();
    this.map = null;
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.group = null;
    this.sun = null;
    this.ref = null;
  }

  /**
   * Show or hide the extruded buildings without tearing the layer down. The
   * geometry stays built (and kept fresh by the move/idle rebuilds), so the
   * "Buildings" toggle flips instantly — the same approach the 3D trees use for
   * their canopy/stems/fire toggles. Re-rendering the shadow map clears any
   * shadows the now-hidden buildings were casting.
   */
  setVisible(visible: boolean) {
    if (this.group) this.group.visible = visible;
    if (this.shadowGround) this.shadowGround.visible = visible;
    if (this.renderer) this.renderer.shadowMap.needsUpdate = true;
    this.map?.triggerRepaint();
  }

  render(_gl: WebGL2RenderingContext, matrix: number[]) {
    if (!this.map || !this.renderer || !this.scene || !this.camera || !this.ref) {
      return;
    }
    if (!this.group || this.group.children.length === 0) return;
    this.camera.projectionMatrix = this.scratch
      .fromArray(matrix)
      .multiply(this.anchor);
    this.renderer.resetState();
    this.renderer.render(this.scene, this.camera);
  }

  private clearMeshes() {
    if (this.shadowGround) {
      this.scene?.remove(this.shadowGround);
      this.shadowGround.geometry.dispose();
      (this.shadowGround.material as THREE.Material).dispose();
      this.shadowGround = null;
    }
    if (!this.group) return;
    for (const child of [...this.group.children]) {
      this.group.remove(child);
      const mesh = child as THREE.Mesh;
      mesh.geometry?.dispose();
      (mesh.material as THREE.Material)?.dispose();
    }
  }

  /**
   * Invisible terrain-following mesh that receives the buildings' shadows.
   * Same construction as FuelLoad3DTrees: clamped to a square around centre,
   * sampled at ~DEM resolution, lifted 0.1 m so it hugs the surface.
   */
  private buildShadowReceiver(view: Bbox, scale: number, grid: ElevGrid) {
    const map = this.map;
    if (!map || !this.scene || !this.ref) return;
    const RECEIVER_MAX_M = 4096;
    const half = (RECEIVER_MAX_M / 2) * scale;
    const x0 = Math.max(view[0], this.ref.x - half);
    const x1 = Math.min(view[2], this.ref.x + half);
    const z0 = Math.max(view[1], this.ref.y - half);
    const z1 = Math.min(view[3], this.ref.y + half);
    if (x0 >= x1 || z0 >= z1) return;
    const widthM = (x1 - x0) / scale;
    const heightM = (z1 - z0) / scale;
    const cx = ((x0 + x1) / 2 - this.ref.x) / scale;
    const cz = ((z0 + z1) / 2 - this.ref.y) / scale;
    const geo = new THREE.PlaneGeometry(
      widthM,
      heightM,
      RECEIVER_SEGMENTS,
      RECEIVER_SEGMENTS
    );
    geo.rotateX(-Math.PI / 2);
    const posAttr = geo.attributes.position;
    for (let j = 0; j < posAttr.count; j++) {
      const mx = this.ref.x + (cx + posAttr.getX(j)) * scale;
      const my = this.ref.y + (cz + posAttr.getZ(j)) * scale;
      const e = elevAt(grid, mx, my);
      posAttr.setY(j, (Number.isNaN(e) ? 0 : e) + 0.1);
    }
    geo.computeVertexNormals();
    const mesh = new THREE.Mesh(
      geo,
      new THREE.ShadowMaterial({ opacity: 0.5, depthWrite: false })
    );
    mesh.position.set(cx, 0, cz);
    mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    this.scene.add(mesh);
    this.shadowGround = mesh;
  }

  /** Aim the sun and fit its ortho shadow camera around the built bounds. */
  private fitSun(
    minX: number,
    maxX: number,
    minZ: number,
    maxZ: number,
    minY: number,
    maxY: number
  ) {
    if (!this.sun) return;
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;
    const cy = (minY + maxY) / 2;
    const r = Math.hypot(maxX - minX, maxZ - minZ) / 2 + (maxY - minY) + 120;
    // Lower sun (smaller y) → longer shadows raking across the landscape.
    const dir = new THREE.Vector3(0.55, 0.78, 0.45).normalize();
    const dist = r * 2 + 300;
    this.sun.position.set(cx + dir.x * dist, cy + dir.y * dist, cz + dir.z * dist);
    this.sun.target.position.set(cx, cy, cz);
    this.sun.target.updateMatrixWorld();
    const cam = this.sun.shadow.camera;
    cam.left = -r;
    cam.right = r;
    cam.top = r;
    cam.bottom = -r;
    cam.near = Math.max(1, dist - r - 100);
    cam.far = dist + r + 100;
    cam.updateProjectionMatrix();
  }

  /**
   * Re-extrude all building footprints from the currently loaded asset tiles.
   * Returns false when terrain tiles aren't loaded yet under some building so
   * the caller keeps the rebuild scheduled.
   */
  rebuild(): boolean {
    const map = this.map;
    if (!map || !this.group) return true;

    this.clearMeshes();
    if (map.getZoom() < MIN_ZOOM) {
      map.triggerRepaint();
      return true;
    }

    let features: GeoJSON.Feature[] = [];
    try {
      features = map.querySourceFeatures(this.sourceId, {
        sourceLayer: SOURCE_LAYER,
      }) as unknown as GeoJSON.Feature[];
    } catch {
      map.triggerRepaint();
      return false;
    }

    const center = mapboxgl.MercatorCoordinate.fromLngLat(map.getCenter());
    const scale = center.meterInMercatorCoordinateUnits();
    this.ref = { x: center.x, y: center.y, scale };
    this.anchor
      .makeTranslation(center.x, center.y, 0)
      .scale(new THREE.Vector3(scale, -scale, scale))
      .multiply(new THREE.Matrix4().makeRotationX(Math.PI / 2));

    // Build only inside the padded viewport — overzoomed tiles cover far more.
    let view: Bbox | null = null;
    const bounds = map.getBounds();
    if (bounds) {
      const nw = mapboxgl.MercatorCoordinate.fromLngLat(bounds.getNorthWest());
      const se = mapboxgl.MercatorCoordinate.fromLngLat(bounds.getSouthEast());
      const padX = (se.x - nw.x) * 0.1;
      const padY = (se.y - nw.y) * 0.1;
      view = [nw.x - padX, nw.y - padY, se.x + padX, se.y + padY];
    }

    // Sample terrain once for both the building bases and the shadow receiver.
    const elevGrid = view ? buildElevGrid(map, view, ELEV_GRID_N) : null;

    // ---- gather footprints (deduped by asset id) -------------------------
    const seen = new Set<string | number>();
    const footprints: Footprint[] = [];
    const collect = (fp: Footprint | null) => {
      if (fp) footprints.push(fp);
    };
    for (const feature of features) {
      if (footprints.length >= MAX_BUILDINGS) break;
      // Buildings only — roads, transmission lines, turbines etc. stay 2D.
      if (!isBuilding(feature.properties)) continue;
      const key =
        feature.id ??
        (feature.properties?.__id as number | undefined) ??
        undefined;
      if (key !== undefined) {
        if (seen.has(key)) continue;
        seen.add(key);
      }
      const geom = feature.geometry;
      if (!geom) continue;
      if (geom.type === "Polygon") {
        collect(ringToFootprint(geom.coordinates[0], center.x, center.y, scale));
      } else if (geom.type === "MultiPolygon") {
        for (const poly of geom.coordinates) {
          collect(ringToFootprint(poly[0], center.x, center.y, scale));
        }
      } else if (geom.type === "Point") {
        collect(pointToFootprint(geom.coordinates, center.x, center.y, scale));
      }
      // LineString / MultiLineString assets (roads etc.) are not buildings.
    }

    if (footprints.length === 0) {
      map.triggerRepaint();
      return true;
    }

    // ---- extrude into one merged geometry --------------------------------
    const terrainOn = Boolean(map.getTerrain());
    let terrainMissing = false;
    const positions: number[] = [];
    const normals: number[] = [];
    const colors: number[] = [];
    // Bright roof-perimeter outlines so buildings read clearly in the landscape.
    const outlinePositions: number[] = [];
    let bMinX = Infinity,
      bMaxX = -Infinity,
      bMinZ = Infinity,
      bMaxZ = -Infinity,
      bMinY = Infinity,
      bMaxY = -Infinity;

    const tmpV2: THREE.Vector2[] = [];
    for (const fp of footprints) {
      // Viewport clip on the centroid (mercator).
      if (view) {
        if (
          fp.mercCx < view[0] ||
          fp.mercCx > view[2] ||
          fp.mercCy < view[1] ||
          fp.mercCy > view[3]
        ) {
          continue;
        }
      }

      // Bilinear terrain from the shared grid (no per-building raycast).
      const elevRaw = elevGrid ? elevAt(elevGrid, fp.mercCx, fp.mercCy) : 0;
      if (Number.isNaN(elevRaw) && terrainOn) {
        // DEM tile not loaded yet — extruding at 0 would bury the building in
        // the hill. Skip and retry once the tile arrives.
        terrainMissing = true;
        continue;
      }
      const base = (Number.isNaN(elevRaw) ? 0 : elevRaw) - 0.3; // sink slightly so it meets the ground
      const height = buildingHeight(fp.area, fp.hkey);
      const top = base + height;

      // Per-building colour jitter (stable from the hash).
      const hH = hash2(fp.hkey, fp.hkey >> 11);
      const hT = hash2(fp.hkey >> 3, fp.hkey >> 17);
      const wall = WALL.clone();
      wall.offsetHSL((hH - 0.5) * 0.04, (hT - 0.5) * 0.08, (hT - 0.5) * 0.06);
      const roof = (hH > 0.55 ? ROOF_TERRA : ROOF_GREY).clone();
      roof.offsetHSL((hH - 0.5) * 0.03, 0, (hT - 0.5) * 0.05);

      const ring = fp.ring;
      const rn = ring.length;

      // Walls: one quad per edge, outward normal pointing away from centroid.
      for (let i = 0, j = rn - 1; i < rn; j = i++) {
        const x0 = ring[j][0],
          z0 = ring[j][1];
        const x1 = ring[i][0],
          z1 = ring[i][1];
        const midX = (x0 + x1) / 2 - fp.cx;
        const midZ = (z0 + z1) / 2 - fp.cz;
        const nl = Math.hypot(midX, midZ) || 1;
        const nx = midX / nl;
        const nz = midZ / nl;
        // Two triangles: (A,B,C) (A,C,D), A=base@j B=base@i C=top@i D=top@j
        const verts = [
          [x0, base, z0],
          [x1, base, z1],
          [x1, top, z1],
          [x0, base, z0],
          [x1, top, z1],
          [x0, top, z0],
        ];
        for (const [vx, vy, vz] of verts) {
          positions.push(vx, vy, vz);
          normals.push(nx, 0, nz);
          colors.push(wall.r, wall.g, wall.b);
        }
      }

      // Roof cap: triangulate the footprint, flat up-normal. triangulateShape
      // keeps the ring's native winding, which for our south-positive z axis
      // usually faces DOWN — the visible top would then be a back face with a
      // flipped normal (lit from below → black roof). Re-wind each triangle so
      // its geometric normal points +y, matching the supplied vertex normal.
      tmpV2.length = 0;
      for (let i = 0; i < rn; i++) tmpV2.push(new THREE.Vector2(ring[i][0], ring[i][1]));
      let tris: number[][] = [];
      try {
        tris = THREE.ShapeUtils.triangulateShape(tmpV2, []);
      } catch {
        tris = [];
      }
      for (const t of tris) {
        const [ia, ib, ic] = t;
        const a = ring[ia];
        const b = ring[ib];
        const c = ring[ic];
        // cross(B-A, C-A).y in (x, z) plane; positive = already faces up.
        const ny =
          (b[1] - a[1]) * (c[0] - a[0]) - (b[0] - a[0]) * (c[1] - a[1]);
        const ordered = ny >= 0 ? [a, b, c] : [a, c, b];
        for (const p of ordered) {
          positions.push(p[0], top, p[1]);
          normals.push(0, 1, 0);
          colors.push(roof.r, roof.g, roof.b);
        }
      }

      // Roof-perimeter highlight outline (slightly above the cap to avoid
      // z-fighting).
      const oy = top + 0.15;
      for (let i = 0, j = rn - 1; i < rn; j = i++) {
        outlinePositions.push(ring[j][0], oy, ring[j][1]);
        outlinePositions.push(ring[i][0], oy, ring[i][1]);
      }

      // Scene bounds for the sun fit.
      for (let i = 0; i < rn; i++) {
        const x = ring[i][0],
          z = ring[i][1];
        if (x < bMinX) bMinX = x;
        if (x > bMaxX) bMaxX = x;
        if (z < bMinZ) bMinZ = z;
        if (z > bMaxZ) bMaxZ = z;
      }
      if (base < bMinY) bMinY = base;
      if (top > bMaxY) bMaxY = top;

      if (!this.capWarned && positions.length / 9 > MAX_BUILDINGS * 50) {
        this.capWarned = true;
      }
    }

    if (positions.length === 0) {
      map.triggerRepaint();
      return !terrainMissing;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(positions, 3)
    );
    geo.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
    geo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    const mesh = new THREE.Mesh(
      geo,
      new THREE.MeshLambertMaterial({
        vertexColors: true,
        side: THREE.DoubleSide,
        // Small uniform lift so shaded walls/roofs never crush to black.
        emissive: new THREE.Color(0x1c1a16),
      })
    );
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    this.group.add(mesh);

    // Bright perimeter outlines — the constant highlight that makes buildings
    // findable in the landscape (same accent as the 2D asset layer).
    if (outlinePositions.length > 0) {
      const lineGeo = new THREE.BufferGeometry();
      lineGeo.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(outlinePositions, 3)
      );
      const lines = new THREE.LineSegments(
        lineGeo,
        new THREE.LineBasicMaterial({ color: HIGHLIGHT })
      );
      lines.frustumCulled = false;
      this.group.add(lines);
    }

    if (view && elevGrid) {
      this.buildShadowReceiver(view, scale, elevGrid);
      this.fitSun(bMinX, bMaxX, bMinZ, bMaxZ, bMinY, bMaxY);
    }
    if (this.renderer) this.renderer.shadowMap.needsUpdate = true;
    map.triggerRepaint();
    return !terrainMissing;
  }
}

// ---------------------------------------------------------------------------
// React wrapper — mounted through the layer's `customComponent` slot
// ---------------------------------------------------------------------------
const Assets3DBuildings: React.FC<Assets3DBuildingsProps> = ({
  sourceId,
  active,
  visible,
}) => {
  const { map } = useMap();
  const layerRef = useRef<AssetBuildingsLayer | null>(null);
  // Latest visibility, read by the attach path without re-running it.
  const visibleRef = useRef(visible);
  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    if (!map || !active) return;

    let cancelled = false;
    let sourceDirty = true;
    let lastViewKey = "";
    let rebuildTimer: number | null = null;
    const layerId = `${sourceId}-3d-buildings`;

    const rebuildIfChanged = () => {
      const layer = layerRef.current;
      if (!layer || cancelled) return;
      const c = map.getCenter();
      const viewKey = `${map.getZoom().toFixed(2)}|${c.lng.toFixed(5)}|${c.lat.toFixed(5)}`;
      if (!sourceDirty && viewKey === lastViewKey) return;
      lastViewKey = viewKey;
      // An incomplete build (DEM tiles still loading) keeps the source dirty so
      // the next idle/sourcedata pass finishes the job.
      sourceDirty = !layer.rebuild();
    };

    const scheduleRebuild = () => {
      if (rebuildTimer !== null) window.clearTimeout(rebuildTimer);
      rebuildTimer = window.setTimeout(() => {
        rebuildTimer = null;
        rebuildIfChanged();
      }, 250);
    };

    const onSourceData = (e: { sourceId?: string; isSourceLoaded?: boolean }) => {
      if (
        (e.sourceId === sourceId || e.sourceId === DEM_SOURCE_ID) &&
        e.isSourceLoaded
      ) {
        sourceDirty = true;
        scheduleRebuild();
      }
    };

    const attach = () => {
      if (cancelled) return;
      if (!map.getLayer(layerId)) {
        const layer = new AssetBuildingsLayer(layerId, sourceId);
        layerRef.current = layer;
        map.addLayer(layer);
      }
      map.on("idle", rebuildIfChanged);
      map.on("moveend", scheduleRebuild);
      map.on("sourcedata", onSourceData);
      layerRef.current?.rebuild();
      // Honour the current "Buildings" toggle for the freshly-built geometry.
      layerRef.current?.setVisible(visibleRef.current);
    };

    // Attach once the style is ready. `map.once("idle", attach)` alone strands
    // here: toggling buildings re-runs this effect while the 2D asset fill is
    // mid-repaint from the same toggle (so isStyleLoaded() is briefly false),
    // and on a static map `idle` may not fire again until the user moves — the
    // buildings then stay invisible until then. Wait on `render` too and nudge a
    // frame so attachment resolves without interaction.
    const attachWhenReady = () => {
      if (cancelled || !map.isStyleLoaded()) return;
      map.off("idle", attachWhenReady);
      map.off("render", attachWhenReady);
      attach();
    };

    if (map.isStyleLoaded()) {
      attach();
    } else {
      map.on("idle", attachWhenReady);
      map.on("render", attachWhenReady);
      map.triggerRepaint();
    }

    return () => {
      cancelled = true;
      if (rebuildTimer !== null) window.clearTimeout(rebuildTimer);
      map.off("idle", attachWhenReady);
      map.off("render", attachWhenReady);
      map.off("idle", rebuildIfChanged);
      map.off("moveend", scheduleRebuild);
      map.off("sourcedata", onSourceData);
      try {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      } catch {
        /* map may be tearing down */
      }
      layerRef.current = null;
    };
  }, [map, active, sourceId]);

  // Toggle the buildings on/off on the already-attached layer instead of tearing
  // it down and re-adding it — a re-attach left them blank until the next map
  // move. This mirrors how the 3D trees flip their parts.
  useEffect(() => {
    layerRef.current?.setVisible(visible);
  }, [visible]);

  return null;
};

export default Assets3DBuildings;
